import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Calendar } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { ARTICLE_STATIC } from "../data/static";

const formatDate = (iso, lang) => {
  const d = new Date(iso);
  return d.toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export default function Destinations() {
  const { t, lang } = useI18n();
  const b = t.blog;

  useEffect(() => {
    document.title = `${b.title} | Jaipur Tours & Travels`;
    window.scrollTo(0, 0);
  }, [b.title]);

  return (
    <div className="pt-24 lg:pt-32 pb-20 bg-[#FAFAF7] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12 lg:mb-16" data-testid="destinations-header">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D1603D]">
            {b.eyebrow}
          </p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-slate-900 leading-tight">
            {b.title}
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600">{b.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {b.articles.map((a, idx) => {
            const stat = ARTICLE_STATIC[a.slug];
            return (
              <Link
                key={a.slug}
                to={`/destinations/${a.slug}`}
                data-testid={`article-card-${a.slug}`}
                className="group block bg-white rounded-3xl overflow-hidden border border-slate-200/80 hover:border-[#D1603D]/40 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-900/5 transition-all duration-300 animate-fade-in-up"
                style={{ animationDelay: `${idx * 80}ms` }}
              >
                <div className="relative h-56 overflow-hidden bg-slate-100">
                  <img
                    src={stat.heroImage}
                    alt={a.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 via-transparent to-transparent" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(stat.publishedAt, lang)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {stat.readMinutes} {b.readTime}
                    </span>
                  </div>
                  <h2 className="font-display text-xl font-semibold text-slate-900 leading-snug group-hover:text-[#D1603D] transition-colors">
                    {a.title}
                  </h2>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-3">
                    {a.excerpt}
                  </p>
                  <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#D1603D] group-hover:gap-2.5 transition-all">
                    {b.readMore} <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
